import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useKioskMode } from '../../contexts/KioskModeContext';
import {
  X,
  ClipboardList,
  UserCircle2,
  LogOut,
  Home,
  UtensilsCrossed,
  ShoppingCart,
  CalendarDays,
  Gamepad2,
  ChevronRight,
  LogIn,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearUser } from '../../store/slices/userSlice';
import { theme } from '../../theme/colors';
import { SITE_CONFIG, getPartnersSignInUrl } from '../../config/siteConfig';
import MarketingLink from '../MarketingLink/MarketingLink';
import { getMarketingSiteUrl } from '../../utils/marketingNavigation';
import restaurantService from '../../services/restaurantService';
import cartService from '../../services/cartService';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import {
  CUSTOMER_FEATURE_TABLE_BOOKING,
  hasCustomerFeature,
  hasVisibleCustomerFeature,
} from '../../utils/planFeatures';
import gamesService from '../../services/gamesService';
import {
  resolveRestaurantAppName,
  saveLastRestaurantAppName,
} from '../../utils/lastRestaurantNavigation';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import './Header.css';

type CustomerNavRowProps = {
  icon: LucideIcon;
  label: string;
  to?: string;
  badge?: number;
  tone?: 'default' | 'danger';
  onClick?: () => void;
};

function CustomerNavSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="customer-nav-section-label">
      <span>{children}</span>
    </div>
  );
}

function CustomerNavRow({
  icon: Icon,
  label,
  to,
  badge,
  tone = 'default',
  onClick,
}: CustomerNavRowProps) {
  const isDanger = tone === 'danger';
  const content = (
    <>
      <span className={`customer-nav-row__icon${isDanger ? ' customer-nav-row__icon--danger' : ''}`}>
        <Icon size={17} strokeWidth={2} aria-hidden />
      </span>
      <span className={`customer-nav-row__label${isDanger ? ' customer-nav-row__label--danger' : ''}`}>
        {label}
      </span>
      {badge && badge > 0 ? (
        <span className="customer-nav-row__badge">{badge}</span>
      ) : (
        <ChevronRight size={16} className="customer-nav-row__chevron" aria-hidden />
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="customer-nav-row" onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className="customer-nav-row" onClick={onClick}>
      {content}
    </button>
  );
}

const Header: React.FC = () => {
  const location = useLocation();
  const { enabled: kioskShell } = useKioskMode();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.user);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isPartnersSubdomain, setIsPartnersSubdomain] = useState(false);
  const [gamesEnabled, setGamesEnabled] = useState(false);
  const [gamesNavVisible, setGamesNavVisible] = useState(false);
  const [loyaltyRewardsEnabled, setLoyaltyRewardsEnabled] = useState(false);
  const [tableBookingEnabled, setTableBookingEnabled] = useState(false);
  const [rewardsNavVisible, setRewardsNavVisible] = useState(false);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [cartCount, setCartCount] = useState(0);

  // Check if current subdomain is "partners"
  useEffect(() => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0]?.toLowerCase();
    setIsPartnersSubdomain(subdomain === 'partners');
  }, []);

  const getPartnersUrl = (): string => getPartnersSignInUrl();

  useEffect(() => {
    document.body.classList.toggle('mobile-drawer-open', mobileDrawerOpen);
    return () => document.body.classList.remove('mobile-drawer-open');
  }, [mobileDrawerOpen]);

  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const handleLogout = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setUserDropdownOpen(false);
    dispatch(clearUser());
    // Full-page server logout clears JWT cookie + session reliably (works even if API POST fails)
    window.location.href = '/logout';
  };

  const getUserDisplayName = (): string => {
    if (!user) return 'User';
    const name = user.firstName || user.userName;
    if (name && name.trim()) {
      return name.trim();
    }
    if (user.emailId) {
      const local = user.emailId.split('@')[0];
      return local || user.emailId;
    }
    return 'User';
  };

  const getUserInitial = (): string => getUserDisplayName().charAt(0).toUpperCase();

  // Extract appName from current URL path
  const getCurrentAppName = (): string | null => {
    const pathMatch = location.pathname.match(/\/restaurants\/([^\/]+)/);
    return pathMatch ? pathMatch[1] : null;
  };

  const getMyOrdersUrl = (): string => '/my-orders';

  const getMyAccountUrl = (): string => {
    const resolved = resolveRestaurantAppName(getCurrentAppName());
    if (resolved) {
      return `/restaurants/${resolved}/my-account`;
    }
    return '/my-account';
  };

  const getSavedAddressesUrl = (): string => {
    const resolved = resolveRestaurantAppName(getCurrentAppName());
    if (resolved) {
      return `/restaurants/${resolved}/saved-addresses`;
    }
    return '/saved-addresses';
  };

  const renderUserProfileSnippet = (variant: 'dropdown' | 'mobile' = 'dropdown') => {
    if (variant === 'dropdown') {
      return (
        <div className="user-dropdown-header user-dropdown-header--info">
          <i className="fa fa-user-circle"></i>
          <div>
            <div className="user-dropdown-name">{getUserDisplayName()}</div>
            {user?.emailId && (
              <div className="user-dropdown-email">{user.emailId}</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mobile-user-info">
        <i className="fa fa-user-circle"></i>
        <div>
          <div className="mobile-user-name">{getUserDisplayName()}</div>
          {user?.emailId && (
            <div className="mobile-user-email">{user.emailId}</div>
          )}
        </div>
      </div>
    );
  };

  const renderAuthenticatedMenuItems = (
    closeMenu: () => void,
    options: { mobile?: boolean } = {}
  ) => {
    const { mobile = false } = options;
    const itemClass = mobile ? 'mobile-nav-link' : 'user-dropdown-item';
    const logoutClass = mobile ? 'mobile-nav-link mobile-logout-btn' : 'user-dropdown-item logout-btn';

    return (
      <>
        {renderUserProfileSnippet(mobile ? 'mobile' : 'dropdown')}
        {!mobile && <div className="user-dropdown-divider" />}
        <Link
          to={getMyOrdersUrl()}
          className={itemClass}
          onClick={closeMenu}
        >
          <i className="fa fa-list-alt"></i>
          <span>Your Orders</span>
        </Link>
        <Link
          to={getMyAccountUrl()}
          className={itemClass}
          onClick={closeMenu}
        >
          <i className="fa fa-user"></i>
          <span>My Account</span>
        </Link>
        <Link
          to={getSavedAddressesUrl()}
          className={itemClass}
          onClick={closeMenu}
        >
          <i className="fa fa-map-marker"></i>
          <span>Address Book</span>
        </Link>
        {!mobile && <div className="user-dropdown-divider" />}
        <button
          type="button"
          className={logoutClass}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLogout(e);
            closeMenu();
          }}
        >
          <i className="fa fa-sign-out"></i>
          <span>Logout</span>
        </button>
      </>
    );
  };

  const appNameFromPath = getCurrentAppName();
  const isRestaurantListingPage = location.pathname === '/restaurants';
  const isAccountPage =
    location.pathname === '/my-orders' ||
    location.pathname.startsWith('/my-orders/') ||
    location.pathname === '/my-account' ||
    location.pathname.endsWith('/my-account') ||
    location.pathname === '/saved-addresses' ||
    location.pathname.endsWith('/saved-addresses');
  const isMyOrdersActive =
    location.pathname === '/my-orders' || location.pathname.includes('/my-orders');
  const isCustomerRestaurantApp = !!appNameFromPath || isRestaurantListingPage || isAccountPage;
  const contextAppName = isCustomerRestaurantApp
    ? resolveRestaurantAppName(appNameFromPath)
    : null;
  const hasRestaurantContext = !!contextAppName;
  const restaurantBase = hasRestaurantContext ? `/restaurants/${contextAppName}` : '';
  const cartPath = hasRestaurantContext
    ? withGuestOrderTypeQuery(`${restaurantBase}/cart`, contextAppName)
    : '';
  const rewardsPageUrl = hasRestaurantContext ? `${restaurantBase}/rewards` : '';
  const rewardsNavUrl = isAuthenticated
    ? rewardsPageUrl
    : `/signin?redirect=${encodeURIComponent(rewardsPageUrl || '/restaurants')}`;
  const signInTo = isRestaurantListingPage
    ? `/signin?redirect=${encodeURIComponent('/restaurants')}`
    : '/signin';
  const isRestaurantHomeActive =
    !!appNameFromPath && location.pathname === restaurantBase;

  useEffect(() => {
    if (!isCustomerRestaurantApp || !contextAppName) {
      setGamesEnabled(false);
      setGamesNavVisible(false);
      setLoyaltyRewardsEnabled(false);
      setTableBookingEnabled(false);
      setRestaurantName(null);
      setCartCount(0);
      return;
    }
    if (appNameFromPath) {
      saveLastRestaurantAppName(appNameFromPath);
    }
    restaurantService
      .getRestaurant(contextAppName)
      .then((res) => {
        setGamesEnabled(hasCustomerFeature(res.features, 'in_app_games'));
        setLoyaltyRewardsEnabled(
          hasVisibleCustomerFeature(res.features, 'loyalty_rewards')
        );
        setTableBookingEnabled(hasCustomerFeature(res.features, CUSTOMER_FEATURE_TABLE_BOOKING));
        setRestaurantName(res.restaurant?.restaurantName ?? null);
      })
      .catch(() => {
        setGamesEnabled(false);
        setGamesNavVisible(false);
        setLoyaltyRewardsEnabled(false);
        setTableBookingEnabled(false);
        setRestaurantName(null);
      });

    cartService
      .getCartCount(contextAppName)
      .then((res) => setCartCount(res.cartCount || 0))
      .catch(() => setCartCount(0));
  }, [isCustomerRestaurantApp, contextAppName, appNameFromPath]);

  useEffect(() => {
    setRewardsNavVisible(loyaltyRewardsEnabled && isAuthenticated);
  }, [loyaltyRewardsEnabled, isAuthenticated]);

  useEffect(() => {
    if (!contextAppName || !gamesEnabled || !isAuthenticated) {
      setGamesNavVisible(false);
      return;
    }
    gamesService
      .getEnabledGames(contextAppName)
      .then((res) => setGamesNavVisible((res.gameTypes || []).length > 0))
      .catch(() => setGamesNavVisible(false));
  }, [contextAppName, gamesEnabled, isAuthenticated]);

  useEffect(() => {
    if (!contextAppName) return;

    const handleCartUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ appName?: string; count?: number }>).detail;
      if (detail?.appName && detail.appName !== contextAppName) return;
      if (typeof detail?.count === 'number') {
        setCartCount(detail.count);
        return;
      }
      cartService
        .getCartCount(contextAppName)
        .then((res) => setCartCount(res.cartCount || 0))
        .catch(() => setCartCount(0));
    };

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdated);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdated);
  }, [contextAppName]);

  useEffect(() => {
    document.body.classList.toggle('restaurant-customer-app', isCustomerRestaurantApp);
    return () => document.body.classList.remove('restaurant-customer-app');
  }, [isCustomerRestaurantApp]);

  if (kioskShell || location.pathname.startsWith('/kiosk/')) {
    return null;
  }

  return (
    <>
      <header
        id="header"
        className={`app-header${isCustomerRestaurantApp ? ' app-header--restaurant' : ''}`}
      >
        <div className="redchili-top">
          <div className="container">
            <div className="header-content">
              <div className="logo-section">
                {isCustomerRestaurantApp ? (
                  <Link
                    to={appNameFromPath ? restaurantBase : '/restaurants'}
                    className="logo-link"
                    onClick={() => setMobileDrawerOpen(false)}
                  >
                    <img
                      src={theme.logo.url}
                      alt={SITE_CONFIG.name}
                      className="logo-img"
                    />
                  </Link>
                ) : isPartnersSubdomain ? (
                  isAuthenticated ? (
                    <a href="/dashboard" className="logo-link" onClick={() => setMobileDrawerOpen(false)}>
                      <img
                        src={theme.logo.url}
                        alt={SITE_CONFIG.name}
                        className="logo-img"
                      />
                    </a>
                  ) : (
                    <Link to="/signin" className="logo-link" onClick={() => setMobileDrawerOpen(false)}>
                      <img
                        src={theme.logo.url}
                        alt={SITE_CONFIG.name}
                        className="logo-img"
                      />
                    </Link>
                  )
                ) : (
                  <MarketingLink href="/" className="logo-link" onClick={() => setMobileDrawerOpen(false)}>
                    <img 
                      src={theme.logo.url} 
                      alt={SITE_CONFIG.name} 
                      className="logo-img"
                    />
                  </MarketingLink>
                )}
              </div>

              {/* Desktop Navigation */}
              <nav className="main-nav desktop-nav">
                {isCustomerRestaurantApp ? (
                  <>
                    <Link
                      to="/restaurants"
                      className={`nav-link${isRestaurantListingPage ? ' nav-link--active' : ''}`}
                    >
                      Discover
                    </Link>
                    {hasRestaurantContext && (
                      <>
                        <Link
                          to={restaurantBase}
                          className={`nav-link${isRestaurantHomeActive ? ' nav-link--active' : ''}`}
                        >
                          Home
                        </Link>
                        <Link to={`${restaurantBase}/menu`} className="nav-link">Menu</Link>
                        <Link to={cartPath} className="nav-link">Cart</Link>
                        {rewardsNavVisible && (
                          <Link to={rewardsPageUrl} className="nav-link">Rewards</Link>
                        )}
                        {gamesNavVisible && (
                          <Link to={`${restaurantBase}/games`} className="nav-link">Games</Link>
                        )}
                      </>
                    )}
                    {isAuthenticated && (
                      <Link
                        to={getMyOrdersUrl()}
                        className={`nav-link${isMyOrdersActive ? ' nav-link--active' : ''}`}
                      >
                        Your Orders
                      </Link>
                    )}
                    {!isAuthenticated ? (
                      <Link to={signInTo} className="nav-link partner-link">
                        <i className="fa fa-sign-in"></i>
                        Login
                      </Link>
                    ) : (
                      <div className="user-menu-wrapper">
                        <button
                          className="user-menu-btn"
                          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                        >
                          <i className="fa fa-user-circle"></i>
                          <span className="user-name">{getUserDisplayName()}</span>
                          <i className={`fa fa-chevron-${userDropdownOpen ? 'up' : 'down'}`}></i>
                        </button>
                        {userDropdownOpen && (
                          <>
                            <div
                              className="dropdown-backdrop"
                              onClick={() => setUserDropdownOpen(false)}
                            />
                            <div className="user-dropdown">
                              {renderAuthenticatedMenuItems(() => setUserDropdownOpen(false))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!isPartnersSubdomain && (
                      <>
                        <MarketingLink href="/" className="nav-link">Home</MarketingLink>
                        <MarketingLink href="/#features" className="nav-link">Features</MarketingLink>
                        <MarketingLink href="/pricing" className="nav-link">Pricing</MarketingLink>
                        <MarketingLink href="/qr-menu" className="nav-link">For Guests</MarketingLink>
                        <MarketingLink href="/blog" className="nav-link">Blog</MarketingLink>
                        <MarketingLink href="/contact" className="nav-link">Contact</MarketingLink>
                      </>
                    )}
                    {isAuthenticated && (
                      <Link
                        to={getMyOrdersUrl()}
                        className={`nav-link${isMyOrdersActive ? ' nav-link--active' : ''}`}
                      >
                        Your Orders
                      </Link>
                    )}
                    {!isPartnersSubdomain && (
                      <a
                        href={getPartnersUrl()}
                        className="nav-link partner-link"
                        target="_self"
                      >
                        <i className="fa fa-building"></i>
                        Join as Partner
                      </a>
                    )}
                    {!isAuthenticated && !isPartnersSubdomain ? (
                      <Link to="/signin" className="nav-link partner-link">
                        <i className="fa fa-sign-in"></i>
                        Customer Login
                      </Link>
                    ) : !isAuthenticated && isPartnersSubdomain ? null : (
                      <div className="user-menu-wrapper">
                        <button
                          className="user-menu-btn"
                          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                        >
                          <i className="fa fa-user-circle"></i>
                          <span className="user-name">{getUserDisplayName()}</span>
                          <i className={`fa fa-chevron-${userDropdownOpen ? 'up' : 'down'}`}></i>
                        </button>
                        {userDropdownOpen && (
                          <>
                            <div
                              className="dropdown-backdrop"
                              onClick={() => setUserDropdownOpen(false)}
                            />
                            <div className="user-dropdown">
                              {renderAuthenticatedMenuItems(() => setUserDropdownOpen(false))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </nav>

              <div className="mobile-header-actions">
                  <button
                    type="button"
                    className="mobile-menu-btn"
                    onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
                    aria-label={mobileDrawerOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={mobileDrawerOpen}
                  >
                    <i className={`fa ${mobileDrawerOpen ? 'fa-times' : 'fa-bars'}`} />
                  </button>
                </div>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`mobile-drawer-overlay ${mobileDrawerOpen ? 'open' : ''}`}
        onClick={() => setMobileDrawerOpen(false)}
      >
        <div
          className={`mobile-drawer${isCustomerRestaurantApp ? ' mobile-drawer--customer' : ''} ${mobileDrawerOpen ? 'open' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {isCustomerRestaurantApp ? (
            <>
              <div className={`customer-drawer-header${isAuthenticated ? '' : ' customer-drawer-header--guest'}`}>
                <button
                  type="button"
                  className="customer-drawer-close"
                  onClick={() => setMobileDrawerOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>

                {isAuthenticated ? (
                  <Link
                    to={getMyAccountUrl()}
                    className="customer-drawer-profile"
                    onClick={closeMobileDrawer}
                  >
                    <div className="customer-drawer-user__avatar" aria-hidden>
                      {getUserInitial()}
                    </div>
                    <div className="customer-drawer-user__meta">
                      <p className="customer-drawer-user__hello">Hi there</p>
                      <p className="customer-drawer-user__name">{getUserDisplayName()}</p>
                      {(user?.emailId || user?.phoneNumber) && (
                        <p className="customer-drawer-user__email">
                          {user.emailId || user.phoneNumber}
                        </p>
                      )}
                    </div>
                    <ChevronRight size={18} className="customer-drawer-profile__chevron" aria-hidden />
                  </Link>
                ) : (
                  <div className="customer-drawer-profile customer-drawer-profile--guest">
                    <div className="customer-drawer-user__avatar" aria-hidden>
                      {(restaurantName || 'P').charAt(0).toUpperCase()}
                    </div>
                    <div className="customer-drawer-user__meta">
                      <p className="customer-drawer-user__name">{restaurantName || 'Menu'}</p>
                      <p className="customer-drawer-user__email">
                        {isRestaurantListingPage
                          ? 'Discover restaurants near you'
                          : 'Sign in to order and track'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <nav className="customer-drawer-body">
                <CustomerNavSectionLabel>Explore</CustomerNavSectionLabel>
                <div className="customer-drawer-section">
                  <CustomerNavRow
                    icon={MapPin}
                    label="Discover Restaurants"
                    to="/restaurants"
                    onClick={closeMobileDrawer}
                  />
                  {hasRestaurantContext && (
                    <>
                      <CustomerNavRow
                        icon={Home}
                        label={restaurantName || 'Restaurant Home'}
                        to={restaurantBase}
                        onClick={closeMobileDrawer}
                      />
                      <CustomerNavRow
                        icon={UtensilsCrossed}
                        label="Menu"
                        to={`${restaurantBase}/menu`}
                        onClick={closeMobileDrawer}
                      />
                      <CustomerNavRow
                        icon={ShoppingCart}
                        label="Cart"
                        to={cartPath}
                        badge={cartCount}
                        onClick={closeMobileDrawer}
                      />
                      {tableBookingEnabled && (
                        <CustomerNavRow
                          icon={CalendarDays}
                          label="Reserve Table"
                          to={`${restaurantBase}/reserve-table`}
                          onClick={closeMobileDrawer}
                        />
                      )}
                    </>
                  )}
                </div>

                {hasRestaurantContext && (
                  <div className="customer-drawer-games-wrap">
                    <Link
                      to={rewardsNavUrl}
                      className="customer-drawer-rewards"
                      onClick={closeMobileDrawer}
                    >
                      <span className="customer-drawer-games__icon" aria-hidden
                        style={{ background: 'rgba(255,255,255,0.18)' }}>
                        <span style={{ fontSize: 22, lineHeight: 1 }}>🎁</span>
                      </span>
                      <div className="customer-drawer-games__copy">
                        <p className="customer-drawer-games__title" style={{ color: '#fff' }}>My Rewards</p>
                        <p className="customer-drawer-games__subtitle" style={{ color: 'rgba(255,255,255,0.82)' }}>
                          Scratch cards &amp; milestone rewards
                        </p>
                      </div>
                    </Link>
                  </div>
                )}

                {hasRestaurantContext && gamesNavVisible && (
                  <div className="customer-drawer-games-wrap">
                    <Link
                      to={`${restaurantBase}/games`}
                      className="customer-drawer-games"
                      onClick={closeMobileDrawer}
                    >
                      <span className="customer-drawer-games__badge">New</span>
                      <span className="customer-drawer-games__icon" aria-hidden>
                        <Gamepad2 size={22} strokeWidth={2} />
                      </span>
                      <div className="customer-drawer-games__copy">
                        <p className="customer-drawer-games__title">Games</p>
                        <p className="customer-drawer-games__subtitle">
                          Spin, guess &amp; win rewards
                        </p>
                      </div>
                    </Link>
                  </div>
                )}

                <div className="customer-drawer-divider" />

                <CustomerNavSectionLabel>Account</CustomerNavSectionLabel>
                <div className="customer-drawer-section">
                  {isAuthenticated ? (
                    <>
                      <CustomerNavRow
                        icon={ClipboardList}
                        label="Your Orders"
                        to={getMyOrdersUrl()}
                        onClick={closeMobileDrawer}
                      />
                      <CustomerNavRow
                        icon={UserCircle2}
                        label="My Account"
                        to={getMyAccountUrl()}
                        onClick={closeMobileDrawer}
                      />
                      <CustomerNavRow
                        icon={MapPin}
                        label="Address Book"
                        to={getSavedAddressesUrl()}
                        onClick={closeMobileDrawer}
                      />
                      <CustomerNavRow
                        icon={LogOut}
                        label="Logout"
                        tone="danger"
                        onClick={() => {
                          handleLogout();
                          closeMobileDrawer();
                        }}
                      />
                    </>
                  ) : (
                    <CustomerNavRow
                      icon={LogIn}
                      label="Login"
                      to={signInTo}
                      onClick={closeMobileDrawer}
                    />
                  )}
                </div>
              </nav>
            </>
          ) : (
            <>
              <div className="mobile-drawer-header">
                <div className="mobile-drawer-brand">
                  <img
                    src={theme.logo.url}
                    alt={SITE_CONFIG.name}
                    className="mobile-drawer-logo"
                  />
                </div>
                <button
                  className="mobile-drawer-close"
                  onClick={() => setMobileDrawerOpen(false)}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>

              <nav className="mobile-drawer-nav">
                {isAuthenticated && (
                  <div className="mobile-drawer-user-section">
                    {renderAuthenticatedMenuItems(closeMobileDrawer, { mobile: true })}
                  </div>
                )}

                {!isPartnersSubdomain && (
                  <a
                    href={getPartnersSignInUrl()}
                    className="mobile-drawer-cta"
                    onClick={closeMobileDrawer}
                  >
                    <i className="fa fa-rocket" aria-hidden="true" />
                    <span>Start free trial</span>
                  </a>
                )}

                {!isPartnersSubdomain && (
                <div className="mobile-drawer-nav-scroll">
                  <div className="mobile-drawer-nav-label">Explore</div>
                  {isAuthenticated && (
                    <Link to={getMyOrdersUrl()} className="mobile-nav-link" onClick={closeMobileDrawer}>
                      <i className="fa fa-list-alt" />
                      <span>Your Orders</span>
                    </Link>
                  )}
                  <MarketingLink href="/" className="mobile-nav-link" onClick={closeMobileDrawer}>
                    <i className="fa fa-home" />
                    <span>Home</span>
                  </MarketingLink>
                  <a href={getMarketingSiteUrl('/#features')} className="mobile-nav-link" onClick={closeMobileDrawer}>
                    <i className="fa fa-th-large" />
                    <span>Features</span>
                  </a>
                  <MarketingLink href="/pricing" className="mobile-nav-link" onClick={closeMobileDrawer}>
                    <i className="fa fa-tags" />
                    <span>Pricing</span>
                  </MarketingLink>
                  <MarketingLink href="/qr-menu" className="mobile-nav-link" onClick={closeMobileDrawer}>
                    <i className="fa fa-mobile" />
                    <span>For Guests</span>
                  </MarketingLink>
                  <MarketingLink href="/blog" className="mobile-nav-link" onClick={closeMobileDrawer}>
                    <i className="fa fa-newspaper-o" />
                    <span>Blog</span>
                  </MarketingLink>
                  <MarketingLink href="/contact" className="mobile-nav-link" onClick={closeMobileDrawer}>
                    <i className="fa fa-envelope" />
                    <span>Contact</span>
                  </MarketingLink>
                </div>
                )}

                <div className="mobile-drawer-footer">
                  {!isPartnersSubdomain && !isAuthenticated && (
                    <Link
                      to="/signin"
                      className="mobile-nav-link mobile-nav-link--secondary"
                      onClick={closeMobileDrawer}
                    >
                      <i className="fa fa-sign-in" />
                      <span>Customer login</span>
                    </Link>
                  )}
                </div>
              </nav>
            </>
          )}
        </div>
      </div>

    </>
  );
};

export default Header;
